import React, { useCallback, useEffect, useRef, useState } from 'react';
import filter from 'lodash/filter';
import { noop } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import ExclamationCircleIcon from '@ad-scripts/presentation/components/lib/icons/exclamation_circle_icon';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import SenderRowActions from './SenderRowActions';
import EmptyState from 'src/presentation/components/pages/proactiveCommunications/components/shared/EmptyState';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import useExistingWhatsAppSenders from './hooks/useExistingWhatsAppSenders';
import { allSucceeded } from 'src/presentation/hooks/useResource';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import ConfigureWhatsAppModal from 'src/presentation/components/pages/channels/whatsapp/ConfigureWhatsAppModal';
import FBSDKIframeLoader, {
  initiateEmbeddedSignup,
  useEmbeddedSignupIframeListener,
} from 'src/presentation/components/pages/channels/whatsapp/FBSDKIframeLoader';
import systemConfigurationResource from 'src/actions/systemConfiguration/systemConfigurationResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import { columnWithTooltip } from 'src/presentation/components/pages/shared/Table';
import { SenderStatus, WhatsAppSender } from 'src/models/Channels/WhatsAppSender';
import whatsAppSenderResource from 'src/actions/channels/whatsAppSenderResource';

const dataAidPrefix = 'whatsapp-senders-page';
const helpDocsLink = 'https://help.gladly.com/docs/set-up-and-configure-whatsapp';
const WHATSAPP_SENDER_ADD_TIMEOUT = 10 * 1000; // 10 seconds

const ColumnConfig = [
  {
    attr: 'alert',
    customRenderer: item => <Alert item={item} />,
    customStyle: () => ({ width: '10px' }),
  },
  { attr: 'phoneNumber', label: 'Phone number', customRenderer: item => <PhoneNumber item={item} />, sorting: true },
  { attr: 'displayName', label: 'Display name', sorting: true },
  {
    attr: 'wabaId',
    label: 'WABA ID',
    customColumnRender: columnWithTooltip({
      dataAid: 'column-order-tooltip',
      message: <>WhatsApp Business Account ID</>,
    }),
    sorting: true,
  },
];

const sortingConfig = { attr: 'wabaId' };

const WhatsAppSenders: TablePageRender = ({ root }) => {
  const dispatch = useDispatch();
  const [showModal, openModal] = useState(false);
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [newSenderInfo, setNewSenderInfo] = useState<WhatsAppSender>();
  const { loadingState: sysConfigLoadingState, value: sysConfig } = useDocumentResource(systemConfigurationResource, {
    fetch: true,
  });
  const iframeRef = useRef(null);
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [wabaId, setWabaId, metaError, setMetaError] = useEmbeddedSignupIframeListener({
    targetOrigin: sysConfig.publicUrl,
  });
  const initiateLogin = (onlyWabaSharing: boolean) => {
    setMetaError(undefined);
    setWabaId(undefined);
    initiateEmbeddedSignup({ iframeRef, targetOrigin: sysConfig.publicUrl, onlyWabaSharing });
  };

  useEffect(() => {
    if (wabaId) {
      newSenderInfo?.setWabaID(wabaId.waba_id);
      dispatch(whatsAppSenderResource.effects.add({}, newSenderInfo, { timeout: WHATSAPP_SENDER_ADD_TIMEOUT }));
    }
  }, [dispatch, wabaId, newSenderInfo]);
  const handleCloseModal = useCallback(() => openModal(false), []);
  const handleOpenConfigModal = useCallback(() => openModal(true), []);
  const { whatsAppSenders, isLoading } = useExistingWhatsAppSenders();
  const action = addNewSenderAction(handleOpenConfigModal);
  const emptyState = <EmptyState title="Connect a WhatsApp Business Account" actionsRender={action} />;

  return (
    <>
      {allSucceeded(sysConfigLoadingState) ? (
        <FBSDKIframeLoader iframeRef={iframeRef} publicUrl={sysConfig.publicUrl} />
      ) : null}
      <TablePage
        actions={action}
        columnConfig={ColumnConfig}
        description={<Description />}
        data-aid={dataAidPrefix}
        errors={[]}
        filterFunc={filterFunc}
        isLoading={isLoading}
        root={root}
        items={whatsAppSenders}
        name="WhatsApp"
        emptyStateRender={emptyState}
        emptyStateFullHeightLayout
        resetErrors={() => noop}
        renderRowActions={props => <SenderRowActions {...props} />}
        initialSorting={sortingConfig}
      />
      {showModal && (
        <ConfigureWhatsAppModal
          handleCloseModal={handleCloseModal}
          setNewSenderInfo={setNewSenderInfo}
          initiateFBLogin={initiateLogin}
        />
      )}
    </>
  );
};

function Description() {
  return (
    <div>
      Connect and manage your WhatsApp Business Accounts (WABAs). To learn more about WABA, visit our{' '}
      <ExternalLink data-aid={`${dataAidPrefix}-developer-help`} to={helpDocsLink} label="Help Docs" />.
    </div>
  );
}

const addNewSenderAction = (handleOpenConfigModal: () => void) => (
  <Button
    data-aid={`${dataAidPrefix}-add-new-sender-button`}
    onClick={handleOpenConfigModal}
    type={ButtonTypes.PRIMARY}
  >
    Connect WhatsApp
  </Button>
);

function Alert({ item }) {
  if (item.status != SenderStatus.ONLINE && isPendingMoreThan24Hrs(item)) {
    return (
      <Tooltip
        data-aid="alert-column-tooltip"
        message={
          <TooltipMessage data-aid="column-tooltip-text">
            WhatsApp business verification request has been rejected.
            <br />
            Contact Gladly support for assistance.
          </TooltipMessage>
        }
      >
        <StyledExclamationCircleIcon />
      </Tooltip>
    );
  }

  return null;
}

function PhoneNumber({ item }) {
  const phoneNumber = formatPhone(item.phoneNumber);

  return (
    <Column>
      {phoneNumber}
      {item.status === SenderStatus.PENDING && !isPendingMoreThan24Hrs(item) && <GrayPill>{item.status}</GrayPill>}
    </Column>
  );
}

function isPendingMoreThan24Hrs(item) {
  const hours = moment().diff(moment(item.createdAt), 'hours');
  return hours > 24;
}

const filterFunc = (items, searchString) => {
  if (!searchString) {
    return items;
  }
  // Remove all hyphens and spaces from the search string because the phone number is in the format +1234567890
  const cleanedSearchString = searchString.replace(/[-\s]/g, '');
  return filter(items, item => {
    return item.phoneNumber.includes(cleanedSearchString);
  });
};

const GrayPill = styled.span`
  border-radius: 12px;
  color: ${p => p.theme.colors.white};
  font-size: ${p => p.theme.fontSize.small};
  margin-left: 8px;
  padding: 2px 8px;
  background-color: ${p => p.theme.colors.gray700}};
`;

const Column = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledExclamationCircleIcon = styled(ExclamationCircleIcon)`
  fill: ${p => p.theme.colors.red400};
  width: 16px;
`;

const TooltipMessage = styled.p`
  text-align: center;
  margin: 0;
  line-height: 162%;
`;

export default WhatsAppSenders;
